import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, CheckCircle2, XCircle, Download, ChevronLeft, ChevronRight, Filter, User, Eye, FileText, Calendar, Files, File } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { type CertificateRequest } from "@/lib/collections/certificateRequests/user/getCertificateRequests";

import { storage } from '@/lib/appwrite';
import { toast } from "react-toastify";
import { getApprovalConfig, isCertificateApproved, isCertificateRejected } from '../config/certificateApprovals';
import { getSupervisorPendingRequests } from '@/lib/collections/certificateRequests/supervisor/getSupervisorCertificateRequest';
import { updateCertificateRequestBySupervisor } from '@/lib/collections/certificateRequests/supervisor/updateCertificateSupervisorRequest';
import { fetchUsersWithProfiles } from '@/lib/collections/workerProfile-Users/fetchCollaboratorsWithProfile';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Link } from '@tanstack/react-router';

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';
type ApprovalStatus = 'pending' | 'approved' | 'rejected';

const CERTIFICATE_TYPES = [
    { value: 'standard', label: 'Certificado Estándar' },
    { value: 'with_salary', label: 'Certificado con Salario' },
    { value: 'job_functions', label: 'Certificado con Funciones' },
] as const;

const ITEMS_PER_PAGE = 6;

const STATUS_CONFIG: Record<ApprovalStatus, {
    label: string;
    variant: 'default' | 'secondary' | 'destructive';
    icon: any;
    className?: string;
}> = {
    pending: {
        label: 'Pendiente',
        variant: 'secondary',
        className: 'bg-yellow-600 dark:bg-yellow-800 text-white dark:text-white',
        icon: Clock,
    },
    approved: {
        label: 'Aprobado',
        variant: 'default',
        icon: CheckCircle2,
        className: 'bg-green-500 hover:bg-green-600 text-white dark:bg-green-600 dark:text-white',
    },
    rejected: {
        label: 'Rechazado',
        variant: 'destructive',
        icon: XCircle,
    },
};

// UTILITY FUNCTIONS
const getOverallStatus = (request: CertificateRequest): ApprovalStatus => {
    if (isCertificateRejected(
        request.type,
        request.statusByRrhh,
        request.statusBySupervisor,
        request.statusByManager
    )) {
        return 'rejected';
    }

    if (isCertificateApproved(
        request.type,
        request.statusByRrhh,
        request.statusBySupervisor,
        request.statusByManager
    )) {
        return 'approved';
    }

    return 'pending';
};

const getTypeLabel = (type: string) => {
    const found = CERTIFICATE_TYPES.find(t => t.value === type);
    return found?.label || type;
};

const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

// SUBCOMPONENTS
const StatusBadge = ({ status }: { status: ApprovalStatus }) => {
    const config = STATUS_CONFIG[status];
    const Icon = config.icon;

    return (
        <Badge
            variant={config.variant}
            className={`flex items-center gap-1 w-fit ${config.className || ''}`}
        >
            <Icon className="w-3 h-3" />
            {config.label}
        </Badge>
    );
};

const ApprovalStep = ({
    status,
    label,
    showLine = true,
    stepNumber // 

}: {
    status: string;
    label: string;
    showLine?: boolean;
    stepNumber: number;  //

}) => {
    const getStepColor = () => {
        if (status === 'approved') return 'bg-green-500';
        if (status === 'rejected') return 'bg-red-500';
        return 'bg-gray-300';
    };

    const getStepIcon = () => {
        if (status === 'approved') return <CheckCircle2 className="w-4 h-4 text-white" />;
        if (status === 'rejected') return <XCircle className="w-4 h-4 text-white" />;
        return <span className="text-xs text-gray-600">{stepNumber}</span>;  // 
    };

    return (
        <>
            <div className="flex items-center gap-1.5 min-w-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getStepColor()}`}>
                    {getStepIcon()}
                </div>
                <span className="text-xs font-medium whitespace-nowrap">{label}</span>
            </div>
            {showLine && (
                <div className={`h-0.5 w-3 sm:w-4 flex-shrink-0 ${status === 'approved' ? 'bg-green-500' : 'bg-gray-300'}`} />
            )}
        </>
    );
};

const ApprovalSteps = ({ request }: { request: CertificateRequest }) => {
    const config = getApprovalConfig(request.type);
    const requiredApprovals = config.requiredApprovals;

    const approvalData: Record<string, { status: string; label: string }> = {
        rrhh: { status: request.statusByRrhh, label: 'RRHH' },
        supervisor: { status: request.statusBySupervisor, label: 'Supervisor' },
        manager: { status: request.statusByManager, label: 'Gerencia' }
    };

    return (
        <div className="flex items-center gap-1.5 sm:gap-2 mt-3">
            {requiredApprovals.map((role, index) => {
                const data = approvalData[role];
                const isLast = index === requiredApprovals.length - 1;

                return (
                    <ApprovalStep
                        key={role}
                        status={data.status}
                        label={data.label}
                        showLine={!isLast}
                        stepNumber={index + 1}
                    />
                );
            })}
        </div>
    );
};

const RejectionNotice = ({ reason }: { reason: string }) => {
    return (
        <div className="border border-red-200 rounded-md p-3">
            <p className="text-xs font-medium text-red-800 dark:text-red-500 mb-1">Motivo del rechazo:</p>
            <p className="text-xs text-red-300 dark:text-gray-400">{reason}</p>
        </div>
    );
};

const EmptyState = () => {
    return (
        <Card className="border-dashed font-outfit">
            <CardContent className="flex flex-col items-center justify-center py-12">
                <User className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No hay solicitudes</h3>
                <p className="text-muted-foreground text-center px-4">
                    No se encontraron solicitudes que requieran tu aprobación
                </p>
                <p className='font-extralight text-xs'>Intenta recargar la pestaña</p>
            </CardContent>
        </Card>
    );
};

const RequestCard = ({
    request,
    onApprove,
    onReject,
    onDownload,
    userName,
    onViewDetails,

}: {
    request: CertificateRequest;
    onApprove: (request: CertificateRequest) => void;
    onReject: (request: CertificateRequest) => void;
    onDownload: (fileId: string, certificateType: string) => void;
    onViewDetails: (request: CertificateRequest) => void;

    userName: string;
}) => {
    const overallStatus = getOverallStatus(request);
    const hasFile = !!request.certificateFileId;
    const rejectionReason = request.commentsRrhh || request.commentsSupervisor || request.commentsManager;

    // Verificar si Supervisor puede actuar sobre esta solicitud
    // Supervisor solo puede actuar si RRHH ya aprobó y el supervisor aún está pendiente
    const canSupervisorAct = request.statusBySupervisor === 'pending'

    return (
        <Card className="hover:shadow-md transition-shadow overflow-hidden">
            <CardHeader className="overflow-hidden">
                <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1 min-w-0 flex-1">
                        <CardTitle className="text-lg break-words overflow-wrap-anywhere">
                            {getTypeLabel(request.type)}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-1">
                            <Clock className="w-3 h-3 flex-shrink-0" />
                            <span className="text-xs">{formatDate(request.requestDate)}</span>
                        </CardDescription>
                        <div className="flex items-center gap-1 mt-1">
                            <User className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                            <span className="text-xs text-muted-foreground truncate">
                                {userName}

                            </span>
                        </div>
                    </div>
                    <StatusBadge status={overallStatus} />
                </div>
            </CardHeader>
            <CardContent className="space-y-4 overflow-hidden">
                <p className="text-sm text-muted-foreground line-clamp-2 break-words overflow-wrap-anywhere">
                    {request.reason}
                </p>

                <ApprovalSteps request={request} />

                {overallStatus === 'rejected' && rejectionReason && (
                    <RejectionNotice reason={rejectionReason} />
                )}

                {canSupervisorAct && overallStatus === 'pending' && (
                    <div className="flex gap-2 pt-2">
                        <Button
                            onClick={() => onApprove(request)}
                            className="flex-1 gap-2 bg-green-600 hover:bg-green-700 cursor-pointer"
                        >
                            <CheckCircle2 className="w-4 h-4" />
                            Aprobar
                        </Button>
                        <Button
                            onClick={() => onReject(request)}
                            className="flex-1 gap-2 cursor-pointer"
                        >
                            <XCircle className="w-4 h-4" />
                            Rechazar
                        </Button>
                    </div>
                )}


                <Button
                    className="w-full gap-2 cursor-pointer "
                    variant="ghost"
                    onClick={() => onViewDetails(request)}
                >
                    <Eye className="w-4 h-4" />
                    Ver Detalles
                </Button>


                {hasFile && (
                    <Button
                        className="w-full gap-2 cursor-pointer"
                        variant="outline"
                        onClick={() => onDownload(request.certificateFileId, request.type)}                    >
                        <Download className="w-4 h-4" />
                        Descargar Certificado
                    </Button>
                )}
            </CardContent>
        </Card>
    );
};

const Pagination = ({
    currentPage,
    totalPages,
    startIndex,
    endIndex,
    total,
    onPageChange,
}: {
    currentPage: number;
    totalPages: number;
    startIndex: number;
    endIndex: number;
    total: number;
    onPageChange: (page: number) => void;
}) => {
    if (totalPages <= 1) return null;

    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
            <p className="text-sm text-muted-foreground">
                Mostrando {startIndex + 1}-{endIndex} de {total}
            </p>
            <div className="flex gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex items-center px-3 text-sm font-medium">
                    {currentPage} / {totalPages}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    <ChevronRight className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
};
const getBucketIdByType = (certificateType: string): string => {
    switch (certificateType) {
        case 'standard':
            return import.meta.env.VITE_BUCKET_ID_STANDAR_EMPLOYMENT_CERTIFICATES;
        case 'with_salary':
            return import.meta.env.VITE_BUCKET_ID_EMPLOYMENT_CERTIFICATE_WITH_SALARY;
        case 'job_functions':
            return import.meta.env.VITE_BUCKET_ID_EMPLOYMENT_CERTIFICATE_wITH_FUNCTIONS;
        default:
            return import.meta.env.VITE_BUCKET_ID_STANDAR_EMPLOYMENT_CERTIFICATES;
    }
};

// MAIN COMPONENT
const SupervisorCertificatesView = () => {
    const { user } = useAuth();

    // State
    const [requests, setRequests] = useState<CertificateRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [detailsDialog, setDetailsDialog] = useState<{ open: boolean; request: CertificateRequest | null; }>({ open: false, request: null });

    const [userFilter, setUserFilter] = useState<string>('all');
    const [userProfiles, setUserProfiles] = useState<Map<string, string>>(new Map());



    // Dialog states
    const [approvalDialog, setApprovalDialog] = useState<{
        open: boolean;
        request: CertificateRequest | null;
        action: 'approve' | 'reject' | null;
    }>({ open: false, request: null, action: null });
    const [rejectionReason, setRejectionReason] = useState('');
    const [processing, setProcessing] = useState(false);

    // Load requests que necesitan aprobación de Supervisor (RRHH ya aprobó)
    useEffect(() => {
        loadRequests();
        loadUserProfiles();
    }, []);


    const handleViewDetails = (request: CertificateRequest) => {
        setDetailsDialog({ open: true, request });
    };
    // AGREGAR después de loadRequests:
    const loadUserProfiles = async () => {
        try {
            const users = await fetchUsersWithProfiles();
            const profileMap = new Map<string, string>();

            users.forEach(user => {
                profileMap.set(user.id, user.name);
            });

            setUserProfiles(profileMap);
        } catch (error) {
            console.error('Error cargando perfiles:', error);
        }
    };

    // AGREGAR después de loadUserProfiles:
    const getUsersWithRequests = () => {
        const uniqueUsers = new Map<string, { id: string; name: string; count: number }>();

        requests.forEach(request => {
            if (uniqueUsers.has(request.userId)) {
                const user = uniqueUsers.get(request.userId)!;
                user.count++;
            } else {
                uniqueUsers.set(request.userId, {
                    id: request.userId,
                    name: userProfiles.get(request.userId) || request.userId,
                    count: 1
                });
            }
        });

        return Array.from(uniqueUsers.values()).sort((a, b) =>
            a.name.localeCompare(b.name)
        );
    };

    const usersWithRequests = getUsersWithRequests();


    const loadRequests = async () => {
        if (!user?.$id) return;

        setLoading(true);
        const result = await getSupervisorPendingRequests(user.$id); // Pasa el userId del supervisor

        if (result.success && result.data) {
            setRequests(result.data);
        } else {
            toast.error('Error al cargar las solicitudes');
        }
        setLoading(false);
    };

    const handleApprove = (request: CertificateRequest) => {
        setApprovalDialog({ open: true, request, action: 'approve' });
    };

    const handleReject = (request: CertificateRequest) => {
        setApprovalDialog({ open: true, request, action: 'reject' });
        setRejectionReason('');
    };

    const confirmApproval = async () => {
        if (!approvalDialog.request || !user?.$id) return;

        // Validar motivo de rechazo si es necesario
        if (approvalDialog.action === 'reject' && !rejectionReason.trim()) {
            toast.error('Debes proporcionar un motivo de rechazo');
            return;
        }

        setProcessing(true);

        try {
            const result = await updateCertificateRequestBySupervisor({
                requestId: approvalDialog.request.$id,
                action: approvalDialog.action!,
                supervisorUserId: user.$id,
                rejectionReason: rejectionReason.trim() || undefined,
                certificateType: approvalDialog.request.type,
                currentStatusByRrhh: approvalDialog.request.statusByRrhh,
                currentStatusByManager: approvalDialog.request.statusByManager
            });

            if (result.success) {
                toast.success(result.message);
                await loadRequests();
                setApprovalDialog({ open: false, request: null, action: null });
                setRejectionReason('');
            } else {
                toast.error(result.error || 'Error al procesar la solicitud');
            }
        } catch (error) {
            toast.error('Error inesperado al procesar la solicitud');
            console.error(error);
        } finally {
            setProcessing(false);
        }
    };

    const handleDownload = (fileId: string, certificateType: string) => {
        try {
            const BUCKET_ID = getBucketIdByType(certificateType);
            const fileUrl = storage.getFileDownload(BUCKET_ID, fileId);
            window.open(fileUrl.toString());
        } catch (error) {
            console.error('Error al descargar:', error);
            toast.error('Error al descargar el archivo');
        }
    };

    // Filtering and pagination
    const filteredRequests = requests.filter(request => {
        const overallStatus = getOverallStatus(request);
        const matchesStatus = statusFilter === 'all' || overallStatus === statusFilter;

        const matchesUser = userFilter === 'all' || request.userId === userFilter;

        const matchesSearch =
            searchTerm === '' ||
            getTypeLabel(request.type).toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.userId.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesStatus && matchesUser && matchesSearch;
    });

    const totalPages = Math.ceil(filteredRequests.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, filteredRequests.length);
    const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

    const handleFilterChange = () => {
        setCurrentPage(1);
    };

    // Get pending count for Supervisor
    const pendingCount = requests.filter(r =>
        r.statusByRrhh === 'approved' && r.statusBySupervisor === 'pending'
    ).length;

    return (

        <><Breadcrumb>
            <BreadcrumbList>
                <BreadcrumbItem>
                    <div className="flex items-center gap-1.5">
                        <Files />
                        <BreadcrumbPage>Solicitudes</BreadcrumbPage>
                    </div>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                        <Link to="/requests">Menu</Link>
                    </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                    <BreadcrumbPage className="hidden sm:block">Supervisor - certificados</BreadcrumbPage>
                    <BreadcrumbPage className="block sm:hidden "> <File className="w-5 h-5" /> </BreadcrumbPage>
                </BreadcrumbItem>
            </BreadcrumbList>
        </Breadcrumb>

            <div className="py-6 pb-24 font-outfit">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h2 className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                            Gestión de Certificados
                        </h2>
                        <p className="text-muted-foreground text-sm">
                            Revisa y aprueba las solicitudes de certificados
                        </p>
                        {pendingCount > 0 && (
                            <Badge variant="secondary" className="mt-2">
                                {pendingCount} pendiente{pendingCount !== 1 ? 's' : ''}
                            </Badge>
                        )}
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    <div className="relative flex-1">
                        <Input
                            placeholder="Buscar por tipo, motivo o usuario..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                handleFilterChange();
                            }}
                            className="pl-9" />
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    </div>


                    <Select
                        value={userFilter}
                        onValueChange={(v) => {
                            setUserFilter(v);
                            handleFilterChange();
                        }}
                    >
                        <SelectTrigger className="w-full sm:w-48">
                            <SelectValue placeholder="Usuario" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">
                                Todos ({requests.length})
                            </SelectItem>
                            {usersWithRequests.map((user) => (
                                <SelectItem key={user.id} value={user.id}>
                                    {user.name} ({user.count})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>


                    <Select
                        value={statusFilter}
                        onValueChange={(v) => {
                            setStatusFilter(v as StatusFilter);
                            handleFilterChange();
                        }}
                    >
                        <SelectTrigger className="w-full sm:w-48">
                            <SelectValue placeholder="Filtrar por estado" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            <SelectItem value="pending">Pendientes</SelectItem>
                            <SelectItem value="approved">Aprobados</SelectItem>
                            <SelectItem value="rejected">Rechazados</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="text-center py-12">
                        <p className="text-muted-foreground">Cargando solicitudes...</p>
                    </div>
                ) : paginatedRequests.length === 0 ? (
                    <EmptyState />
                ) : (
                    <>
                        <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
                            {paginatedRequests.map((request) => (
                                <RequestCard
                                    key={request.$id}
                                    request={request}
                                    onApprove={handleApprove}
                                    onReject={handleReject}
                                    onDownload={handleDownload}
                                    userName={userProfiles.get(request.userId) || request.userId}
                                    onViewDetails={handleViewDetails} />
                            ))}
                        </div>

                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            startIndex={startIndex}
                            endIndex={endIndex}
                            total={filteredRequests.length}
                            onPageChange={setCurrentPage} />
                    </>
                )}

                {/* Approval/Rejection Dialog */}
                <Dialog
                    open={approvalDialog.open}
                    onOpenChange={(open) => {
                        if (!processing) {
                            setApprovalDialog(prev => ({ ...prev, open }));
                        }
                    }}
                >
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>
                                {approvalDialog.action === 'approve'
                                    ? '¿Aprobar solicitud?'
                                    : '¿Rechazar solicitud?'}
                            </DialogTitle>
                            <DialogDescription>
                                {approvalDialog.action === 'approve'
                                    ? 'Esta acción aprobará la solicitud desde Supervisión.'
                                    : 'El usuario será notificado del rechazo de su solicitud.'}
                            </DialogDescription>
                        </DialogHeader>

                        {approvalDialog.action === 'reject' && (
                            <div className="grid gap-2 py-4">
                                <label className="text-sm font-medium">
                                    Motivo del rechazo <span className="text-red-500">*</span>
                                </label>
                                <Textarea
                                    placeholder="Explica por qué se rechaza la solicitud..."
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    rows={3}
                                    disabled={processing} />
                            </div>
                        )}



                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setApprovalDialog({ open: false, request: null, action: null });
                                    setRejectionReason('');
                                }}
                                disabled={processing}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={confirmApproval}
                                disabled={processing ||
                                    (approvalDialog.action === 'reject' && !rejectionReason.trim())}
                                variant={approvalDialog.action === 'reject' ? 'destructive' : 'default'}
                                className={approvalDialog.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                            >
                                {processing ? 'Procesando...' : 'Confirmar'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>


                {/* Dialog de Detalles */}
                <Dialog
                    open={detailsDialog.open}
                    onOpenChange={(open) => setDetailsDialog(prev => ({ ...prev, open }))}
                >
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>Detalles de la Solicitud</DialogTitle>
                            <DialogDescription>
                                Información completa de la solicitud de certificado
                            </DialogDescription>
                        </DialogHeader>

                        {detailsDialog.request && (
                            <div className="space-y-4 py-4">
                                {/* Información del Usuario */}
                                <div className="space-y-2">
                                    <h3 className="font-semibold flex items-center gap-2">
                                        <User className="w-4 h-4" />
                                        Usuario
                                    </h3>
                                    <p className="text-sm text-muted-foreground pl-6">
                                        {userProfiles.get(detailsDialog.request.userId) || detailsDialog.request.userId}
                                    </p>
                                </div>

                                {/* Tipo de Certificado */}
                                <div className="space-y-2">
                                    <h3 className="font-semibold flex items-center gap-2">
                                        <FileText className="w-4 h-4" />
                                        Tipo de Certificado
                                    </h3>
                                    <p className="text-sm text-muted-foreground pl-6">
                                        {getTypeLabel(detailsDialog.request.type)}
                                    </p>
                                </div>

                                {/* Fecha de Solicitud */}
                                <div className="space-y-2">
                                    <h3 className="font-semibold flex items-center gap-2">
                                        <Calendar className="w-4 h-4" />
                                        Fecha de Solicitud
                                    </h3>
                                    <p className="text-sm text-muted-foreground pl-6">
                                        {formatDate(detailsDialog.request.requestDate)}
                                    </p>
                                </div>

                                {/* Motivo */}
                                <div className="space-y-2">
                                    <h3 className="font-semibold">Motivo de la Solicitud</h3>
                                    <p className="text-sm text-muted-foreground pl-6">
                                        {detailsDialog.request.reason}
                                    </p>
                                </div>

                                {/* Estados de Aprobación */}
                                {/* <div className="space-y-2">
                <h3 className="font-semibold">Estado de Aprobaciones</h3>
                <div className="pl-6 space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-sm">RRHH:</span>
                        <StatusBadge status={detailsDialog.request.statusByRrhh as any} />
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-sm">Supervisor:</span>
                        <StatusBadge status={detailsDialog.request.statusBySupervisor as any} />
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-sm">Gerencia:</span>
                        <StatusBadge status={detailsDialog.request.statusByManager as any} />
                    </div>
                </div>
            </div> */}

                                {/* Comentarios si existen */}
                                {(detailsDialog.request.commentsRrhh ||
                                    detailsDialog.request.commentsSupervisor ||
                                    detailsDialog.request.commentsManager) && (
                                        <div className="space-y-2">
                                            <h3 className="font-semibold">Comentarios</h3>
                                            <div className="pl-6 space-y-2">
                                                {detailsDialog.request.commentsRrhh && (
                                                    <div className="border-l-2 border-blue-500 pl-3">
                                                        <p className="text-xs font-medium">RRHH:</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {detailsDialog.request.commentsRrhh}
                                                        </p>
                                                    </div>
                                                )}
                                                {detailsDialog.request.commentsSupervisor && (
                                                    <div className="border-l-2 border-purple-500 pl-3">
                                                        <p className="text-xs font-medium">Supervisor:</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {detailsDialog.request.commentsSupervisor}
                                                        </p>
                                                    </div>
                                                )}
                                                {detailsDialog.request.commentsManager && (
                                                    <div className="border-l-2 border-orange-500 pl-3">
                                                        <p className="text-xs font-medium">Gerencia:</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {detailsDialog.request.commentsManager}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                {/* Estado General */}
                                <div className="space-y-2">
                                    <h3 className="font-semibold">Estado General</h3>
                                    <div className="pl-6">
                                        <StatusBadge status={getOverallStatus(detailsDialog.request)} />
                                    </div>
                                </div>
                            </div>
                        )}

                        <DialogFooter>
                            <Button onClick={() => setDetailsDialog({ open: false, request: null })}>
                                Cerrar
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div></>
    );
};

export default SupervisorCertificatesView;